import { Button } from '@leadsales/design-system/react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import FormDialog, {
  FormField,
} from 'src/components/FormDialog/FormDialog.component';
import MoneyInput from 'src/components/MoneyInput/MoneyInput.component';
import LeadCloseOptionsDialog from './LeadCloseOptionsDialog.component';
import {
  CLOSE_MAX_NOTE,
  CLOSE_MAX_OTHER_TEXT,
  CLOSE_MAX_WHAT_SOLD,
} from './LeadClose.constants';
import {
  CLOSE_OTHER_OPTION,
  closeErrorCopyKey,
  formFromResult,
  currencyDecimals,
  hasLiveSale,
  initialCloseForm,
  tCloseForm,
  validateCloseForm,
} from './LeadClose.functions';
import { useLeadClose } from './useLeadClose.hook';
import './LeadCloseDialog.styles.scss';

const ID = 'lead-close-form';

function today(): string {
  return new Date().toLocaleDateString('en-CA');
}

/**
 * Mounts one form per opening: keyed by the opening, so a new one never shows
 * the values or the errors of the last.
 */
export default function LeadCloseDialog(): JSX.Element | null {
  const { form } = useLeadClose();
  // A conflict swaps in the current result: its revision starts the values again.
  return form ? (
    <LeadCloseForm key={`${form.id}:${form.edit?.revision ?? ''}`} />
  ) : null;
}

/** Where the focus goes on close when what opened the form is gone (the bar's menu item). */
function returnFocus(): HTMLElement | null {
  return document.querySelector<HTMLElement>('[data-lead-close-return]');
}

/**
 * The one result form (U4): Vendido, Perdido or Resuelto for the lead the
 * provider opened it for. It waits for the workspace's settings and the
 * lead's summary before it can be sent, so it never guesses a currency or a
 * first sale. Drawn over the page, outside the chat modal's box (KTD20).
 */
function LeadCloseForm(): JSX.Element | null {
  const { t } = useTranslation('chat/chat');
  const {
    form,
    settings,
    summaryOf,
    loadSummary,
    reloadSettings,
    closeForm,
    submitForm,
  } = useLeadClose();
  const held = form ? summaryOf(form.lead.id) : null;
  // An edit answers about the old amount the result was saved with, not today's.
  const summary =
    held && form?.edit && held.data
      ? {
          ...held,
          data: { ...held.data, prior_amount: form.edit.prior_amount },
        }
      : held;
  // On the data: a settings reload (a conflict) keeps the form's fields.
  const ready = !!settings.data && summary?.status === 'ready';
  const [values, setValues] = useState<tCloseForm | null>(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (form && ready && values === null && settings.data && summary?.data) {
      setValues(
        form.edit
          ? formFromResult(form.edit)
          : initialCloseForm({
              type: form.type,
              settings: settings.data,
              summary: summary.data,
              leadValue: form.lead.value,
              today: today(),
            }),
      );
    }
  }, [form, ready, values, settings.data, summary?.data]);

  // The first field takes the focus once the form can be filled; before that
  // the dialog only shows its loading line.
  const filled = values !== null;
  const firstField = form?.type === 'sold' ? `${ID}-what-sold` : `${ID}-reason`;
  useEffect(() => {
    if (filled) document.getElementById(firstField)?.focus();
  }, [filled, firstField]);

  // Settings read again after a refusal (currency_not_allowed) may no longer
  // allow the picked currency: the form goes back to the main one.
  const currency = values?.currency;
  useEffect(() => {
    const data = settings.data;
    if (!data || !currency) return;
    const allowed = data.multi_currency_enabled
      ? data.currencies.map((entry) => entry.code)
      : [data.sales_currency];
    if (!allowed.includes(currency)) {
      setValues((current) =>
        current ? { ...current, currency: data.sales_currency } : current,
      );
    }
  }, [settings.data, currency]);

  // "Editar opciones" (or a colleague) may remove the picked option or turn
  // "Otro" off: the pick goes, so the form asks for a reason again (U17).
  const picked = values?.optionUuid;
  const formType = form?.type;
  useEffect(() => {
    const data = settings.data;
    if (!data || !picked || (formType !== 'lost' && formType !== 'resolved'))
      return;
    const list = formType === 'lost' ? data.lost : data.resolved;
    const otherOn =
      formType === 'lost' ? data.allow_other_lost : data.allow_other_resolved;
    const still =
      picked === CLOSE_OTHER_OPTION
        ? otherOn
        : list.some((option) => option.uuid === picked);
    if (!still)
      setValues((current) =>
        current ? { ...current, optionUuid: null } : current,
      );
  }, [settings.data, picked, formType]);

  if (!form) return null;

  const type = form.type;
  const busy = form.saving;
  const errors =
    values && settings.data && summary?.data
      ? validateCloseForm(values, {
          settings: settings.data,
          summary: summary.data,
        })
      : {};
  const shown = attempted ? errors : {};
  const options =
    type === 'lost'
      ? (settings.data?.lost ?? [])
      : (settings.data?.resolved ?? []);
  const allowOther =
    type === 'lost'
      ? settings.data?.allow_other_lost
      : settings.data?.allow_other_resolved;
  const firstSale =
    type === 'sold' &&
    summary?.data &&
    !hasLiveSale(summary.data) &&
    Boolean(form.lead.value);

  function set<K extends keyof tCloseForm>(key: K, value: tCloseForm[K]) {
    setValues((current) => (current ? { ...current, [key]: value } : current));
  }

  function submit() {
    setAttempted(true);
    if (!values || !ready || Object.keys(errors).length > 0) return;
    void submitForm(values);
  }

  function message(field: keyof tCloseForm): string | undefined {
    const reason = shown[field];
    return reason ? t(closeErrorCopyKey(reason, type)) : undefined;
  }

  const subtitle = [form.lead.name, form.lead.stageName]
    .filter(Boolean)
    .join(' · ');

  return createPortal(
    <>
      <FormDialog
        id={ID}
        title={t(
          form.edit
            ? `leadClose.form.title.edit.${type}`
            : `leadClose.form.title.${type}`,
        )}
        description={subtitle}
        busy={busy}
        closeLabel={t('leadClose.form.close')}
        fallbackFocus={returnFocus}
        // The X and Escape say so; a click on the backdrop is `other`.
        onClose={(via) => closeForm(via === 'backdrop' ? 'other' : via)}
        onSubmit={submit}
        actions={
          <>
            <Button
              type="button"
              color="ghost"
              size="sm"
              disabled={busy}
              onClick={() => closeForm('cancel')}
            >
              {t('leadClose.form.cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              size="sm"
              loading={busy}
              disabled={!ready || !values}
            >
              {t(
                form.edit
                  ? 'leadClose.form.submit.edit'
                  : `leadClose.form.submit.${type}`,
              )}
            </Button>
          </>
        }
      >
        {summary?.status === 'failed' || settings.status === 'failed' ? (
          <div className="LeadCloseDialog__LoadFailed" role="alert">
            <p>{t('leadClose.form.loadFailed')}</p>
            <Button
              type="button"
              color="ghost"
              size="sm"
              onClick={() => {
                if (settings.status === 'failed') reloadSettings();
                if (summary?.status === 'failed') loadSummary(form.lead.id);
              }}
            >
              {t('leadClose.form.retry')}
            </Button>
          </div>
        ) : null}

        {!ready &&
        summary?.status !== 'failed' &&
        settings.status !== 'failed' ? (
          <p className="LeadCloseDialog__Loading" aria-live="polite">
            {t('leadClose.form.loading')}
          </p>
        ) : null}

        {values && ready ? (
          <div className="LeadCloseDialog__Fields ph-no-capture">
            {type === 'sold' ? (
              <>
                <FormField
                  id={`${ID}-what-sold`}
                  label={t('leadClose.form.whatSold')}
                  error={message('whatSold')}
                >
                  <input
                    id={`${ID}-what-sold`}
                    className="FormDialog__Input"
                    maxLength={CLOSE_MAX_WHAT_SOLD}
                    value={values.whatSold}
                    disabled={busy}
                    onChange={(event) => set('whatSold', event.target.value)}
                  />
                </FormField>
                <div className="LeadCloseDialog__Row">
                  <MoneyInput
                    // The form checks and explains the amount (KTD25): the
                    // input filter would turn "1,500" into 1.50 silently.
                    filterInput={false}
                    id={`${ID}-amount`}
                    label={t('leadClose.form.amount')}
                    value={values.amount}
                    onChange={(raw) => set('amount', raw)}
                    currencySymbol="$"
                    currencyCode={
                      settings.data?.multi_currency_enabled
                        ? undefined
                        : values.currency
                    }
                    maxDecimals={currencyDecimals(
                      settings.data!,
                      values.currency,
                    )}
                    error={message('amount')}
                    helper={
                      firstSale ? t('leadClose.form.amountHelper') : undefined
                    }
                    disabled={busy}
                  />
                  {settings.data?.multi_currency_enabled ? (
                    <FormField
                      id={`${ID}-currency`}
                      label={t('leadClose.form.currency')}
                    >
                      <select
                        id={`${ID}-currency`}
                        className="FormDialog__Input"
                        value={values.currency}
                        disabled={busy}
                        onChange={(event) =>
                          set('currency', event.target.value)
                        }
                      >
                        {settings.data.currencies.map((currency) => (
                          <option key={currency.code} value={currency.code}>
                            {currency.code}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  ) : null}
                </div>
                <FormField
                  id={`${ID}-sale-date`}
                  label={t('leadClose.form.saleDate')}
                >
                  <input
                    id={`${ID}-sale-date`}
                    type="date"
                    className="FormDialog__Input"
                    value={values.saleDate}
                    max={today()}
                    disabled={busy}
                    onChange={(event) => set('saleDate', event.target.value)}
                  />
                </FormField>
                {summary?.data?.prior_amount ? (
                  <fieldset className="LeadCloseDialog__Prior">
                    <legend>
                      {t('leadClose.form.prior.question', {
                        amount: summary.data.prior_amount,
                      })}
                    </legend>
                    {(['kept', 'replaced'] as const).map((choice) => (
                      <label key={choice} className="LeadCloseDialog__Choice">
                        <input
                          type="radio"
                          name={`${ID}-prior`}
                          checked={values.priorHandling === choice}
                          disabled={busy}
                          onChange={() => set('priorHandling', choice)}
                        />
                        {t(`leadClose.form.prior.${choice}`)}
                      </label>
                    ))}
                    {message('priorHandling') ? (
                      <p className="FormDialog__FieldError" role="alert">
                        {message('priorHandling')}
                      </p>
                    ) : null}
                  </fieldset>
                ) : null}
              </>
            ) : (
              <>
                <FormField
                  id={`${ID}-reason`}
                  label={t(`leadClose.form.reason.${type}`)}
                  error={message('optionUuid')}
                >
                  <select
                    id={`${ID}-reason`}
                    className="FormDialog__Input"
                    value={values.optionUuid ?? ''}
                    disabled={busy}
                    onChange={(event) =>
                      set('optionUuid', event.target.value || null)
                    }
                  >
                    <option value="">{t('leadClose.form.choose')}</option>
                    {options.map((option) => (
                      <option key={option.uuid} value={option.uuid}>
                        {option.label}
                      </option>
                    ))}
                    {allowOther ? (
                      <option value={CLOSE_OTHER_OPTION}>
                        {t('leadClose.form.other')}
                      </option>
                    ) : null}
                  </select>
                </FormField>
                {settings.data?.can_edit ? (
                  // U17: Owners and Admins fix the list without leaving the form.
                  <button
                    id={`${ID}-edit-options`}
                    type="button"
                    className="ls-btn ls-btn-link ls-btn-sm LeadCloseDialog__OptionsLink"
                    disabled={busy}
                    onClick={() => setOptionsOpen(true)}
                  >
                    {t('leadClose.settings.editLink')}
                  </button>
                ) : null}
                {values.optionUuid === CLOSE_OTHER_OPTION ? (
                  <FormField
                    id={`${ID}-other`}
                    label={t(`leadClose.form.otherText.${type}`)}
                    error={message('otherText')}
                  >
                    <input
                      id={`${ID}-other`}
                      className="FormDialog__Input"
                      maxLength={CLOSE_MAX_OTHER_TEXT}
                      value={values.otherText}
                      disabled={busy}
                      onChange={(event) => set('otherText', event.target.value)}
                    />
                  </FormField>
                ) : null}
              </>
            )}

            <FormField
              id={`${ID}-note`}
              label={t('leadClose.form.note')}
              error={message('note')}
            >
              <textarea
                id={`${ID}-note`}
                className="FormDialog__Input LeadCloseDialog__Note"
                maxLength={CLOSE_MAX_NOTE}
                rows={3}
                value={values.note}
                disabled={busy}
                aria-describedby={`${ID}-note-helper`}
                onChange={(event) => set('note', event.target.value)}
              />
            </FormField>
            <p id={`${ID}-note-helper`} className="LeadCloseDialog__Helper">
              {t('leadClose.form.noteHelper')}
            </p>

            {/* An edit corrects the result; archiving is the bar's button. */}
            {form.edit ? null : (
              <label className="LeadCloseDialog__Choice">
                <input
                  type="checkbox"
                  checked={values.archive}
                  disabled={busy}
                  onChange={(event) => set('archive', event.target.checked)}
                />
                {t('leadClose.form.archive')}
              </label>
            )}
          </div>
        ) : null}

        {form.error ? (
          <p className="FormDialog__Error" role="alert">
            {t(closeErrorCopyKey(form.error, type))}
          </p>
        ) : null}
      </FormDialog>
      {/* A sibling, not a child: its submit must not bubble to the result form. */}
      {optionsOpen ? (
        <LeadCloseOptionsDialog
          onClose={() => {
            setOptionsOpen(false);
            document.getElementById(`${ID}-edit-options`)?.focus();
          }}
        />
      ) : null}
    </>,
    document.body,
  );
}
